import { orange, red } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';

import { ParagonColors } from '@paragon/client-tools';

const error = red;

declare module '@mui/material/styles' {
  interface Palette {
    avatar: {
      main: string;
    };

    backContrast: {
      main: string;
      light: string;
      dark: string;
    };

    buttonColor: {
      main: string;
    };

    canvas: {
      main: string;
    };

    logoText: {
      main: string;
    };
  }

  interface PaletteOptions {
    avatar: {
      main: string;
    };

    backContrast: {
      main: string;
      light: string;
      dark: string;
    };

    buttonColor: {
      main: string;
    };

    canvas: {
      main: string;
    };

    logoText: {
      main: string;
    };
  }
}

export const darkTheme = createTheme({
  typography: {
    fontFamily: 'Roboto Condensed, Figtree, sans-serif',
    // Figtree Semi-Bold for the App Header/Pargon name
    h1: {
      fontSize: '1.2rem',
      fontFamily: 'Figtree',
      fontWeight: 600,
      webkitFontSmoothing: 'antialiased',
    },
    // Figtree Regular for Primary Headings
    h2: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h3: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h4: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h5: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h6: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Regular for Standard Headings and Actions
    subtitle1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    subtitle2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    button: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    overline: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Light for captions
    caption: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 300,
      webkitFontSmoothing: 'antialiased',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: '#D5D5D5',
        },
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            backgroundColor: 'transparent',
            borderColor: `#b1b1b1`,
            color: `#D5D5D5`,
          },
        },
      ],
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: ParagonColors.green,
    },
    secondary: {
      main: ParagonColors.paragold,
    },
    error: {
      main: error[400],
    },
    avatar: {
      main: ParagonColors.orange,
    },
    background: {
      default: ParagonColors.background,
      paper: ParagonColors.background,
    },
    backContrast: {
      main: '#2d2d2d',
      light: '#3A3A3A',
      dark: '#222222',
    },
    buttonColor: {
      main: '#fff',
    },
    canvas: {
      main: ParagonColors.canvasDark,
    },
    logoText: {
      main: '#fff',
    },
  },
});

export const lightTheme = createTheme({
  typography: {
    fontFamily: 'Roboto Condensed, Figtree, sans-serif',
    // Figtree Semi-Bold for the App Header/Pargon name
    h1: {
      fontSize: '1.2rem',
      fontFamily: 'Figtree',
      fontWeight: 600,
      webkitFontSmoothing: 'antialiased',
    },
    // Figtree Regular for Primary Headings
    h2: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h3: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h4: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h5: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h6: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Regular for Standard Headings and Actions
    subtitle1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    subtitle2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    button: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    overline: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Light for captions
    caption: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 300,
      webkitFontSmoothing: 'antialiased',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: '#EEEEEE',
        },
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            backgroundColor: 'transparent',
            borderColor: `#333`,
            color: `#333`,
          },
        },
      ],
    },
  },
  palette: {
    primary: {
      main: ParagonColors.green,
    },
    secondary: {
      main: ParagonColors.paragold,
    },
    error: {
      main: error[500],
    },
    avatar: {
      main: orange[300],
    },
    background: {
      default: ParagonColors.background,
      paper: ParagonColors.background,
    },
    backContrast: {
      main: '#eee',
      light: '#fff',
      dark: '#ccc',
    },
    buttonColor: {
      main: '#3A3A3A',
    },
    canvas: {
      main: ParagonColors.canvasLight,
    },
    logoText: {
      main: '#303030',
    },
  },
});
